import { useTranslation } from 'react-i18next';

import ProductRow from './components/ProductRow/ProductRow';
import EmptyState from '../../../../../shared/components/Globals/EmptyState/EmptyState';
import type { ICensusValidationResult, IQuickCensusFileResponse } from '../../../../../shared/interfaces/quotes';
import { TRANSLATIONS_QUOTES } from '../../../../../shared/translations/quotes';
import { CommercialAddonType, type AgeInputMode, type TobaccoStatus } from '../../../../../shared/types/quotes';
import type { IProductGroup, SelectionState, IQuoteSummary } from '../../../../../shared/utils/quotes/quotes';
import PhaseHeader from '../../components/PhaseHeader/PhaseHeader';
import './ProductsSection.scss';

export type ProductsSectionVariant = 'employerPaid' | 'voluntary';

interface IProductsSectionProps {
  variant: ProductsSectionVariant;
  number: string;
  groups: IProductGroup[];
  selection: SelectionState;
  selectedCount: number;
  age: number;
  employeesSize: number;
  averageAge: string;
  averageAgeError?: string | null;
  error?: string | null;
  disabled?: boolean;
  /** True when `disabled` is caused by "no plan chosen yet" rather than an invalid headcount. */
  blockedByPlan?: boolean;
  onToggle: (key: string) => void;
  onSelectAddon: (key: string, addonId: number) => void;
  onTobacco: (key: string, status: TobaccoStatus) => void;
  onAverageAgeChange: (value: string) => void;
  onAgeInputModeChange: (key: string, mode: AgeInputMode) => void;
  onCensusApply: (
    key: string,
    avgAge: number,
    summary: ICensusValidationResult['summary'],
    fileObj?: IQuickCensusFileResponse,
    fileName?: string,
    rowCount?: number
  ) => void;
  onCensusRemove: (key: string) => void;
  onUpdateHeadcount?: (next: number) => void;
  summary?: IQuoteSummary;
  censusFile?: IQuickCensusFileResponse | null;
}

const VARIANT_CONFIG: Record<
  ProductsSectionVariant,
  {
    types: CommercialAddonType[];
    titleKey: string;
    selectedKey: string;
    hintKey: string;
    planHintKey: string;
    emptyTitleKey: string;
    emptyDescriptionKey: string;
    showTypeHeaders: boolean;
  }
> = {
  employerPaid: {
    types: [CommercialAddonType.Insurance, CommercialAddonType.Wellness],
    titleKey: TRANSLATIONS_QUOTES.create.employerPaidProducts.title,
    selectedKey: TRANSLATIONS_QUOTES.create.employerPaidProducts.selected,
    hintKey: TRANSLATIONS_QUOTES.create.employerPaidProducts.completeGroupHint,
    planHintKey: TRANSLATIONS_QUOTES.create.employerPaidProducts.choosePlanHint,
    emptyTitleKey: TRANSLATIONS_QUOTES.create.employerPaidProducts.emptyTitle,
    emptyDescriptionKey: TRANSLATIONS_QUOTES.create.employerPaidProducts.emptyDescription,
    showTypeHeaders: true
  },
  voluntary: {
    types: [CommercialAddonType.Voluntary],
    titleKey: TRANSLATIONS_QUOTES.create.voluntaryBenefits.title,
    selectedKey: TRANSLATIONS_QUOTES.create.voluntaryBenefits.selected,
    hintKey: TRANSLATIONS_QUOTES.create.voluntaryBenefits.completeGroupHint,
    planHintKey: TRANSLATIONS_QUOTES.create.voluntaryBenefits.choosePlanHint,
    emptyTitleKey: TRANSLATIONS_QUOTES.create.voluntaryBenefits.emptyTitle,
    emptyDescriptionKey: TRANSLATIONS_QUOTES.create.voluntaryBenefits.emptyDescription,
    showTypeHeaders: false
  }
};

const TYPE_LABEL_KEYS: Record<CommercialAddonType, string> = {
  [CommercialAddonType.Insurance]: TRANSLATIONS_QUOTES.create.products.insurance,
  [CommercialAddonType.Wellness]: TRANSLATIONS_QUOTES.create.products.wellness,
  [CommercialAddonType.Voluntary]: TRANSLATIONS_QUOTES.create.products.voluntary
};

const ProductsSection = ({
  variant,
  number,
  groups,
  selection,
  selectedCount,
  age,
  employeesSize,
  averageAge,
  averageAgeError,
  error,
  disabled = false,
  blockedByPlan = false,
  onToggle,
  onSelectAddon,
  onTobacco,
  onAverageAgeChange,
  onAgeInputModeChange,
  onCensusApply,
  onCensusRemove,
  onUpdateHeadcount,
  summary,
  censusFile
}: IProductsSectionProps) => {
  const { t } = useTranslation();
  const config = VARIANT_CONFIG[variant];
  const hasAnyGroups = config.types.some((type) => groups.some((group) => group.type === type));

  if (!hasAnyGroups) {
    if (!error) return null;
    return (
      <section className="quote-products">
        <PhaseHeader number={number} className="quote-products__header" title={t(config.titleKey)} />
        <div className="quote-products__content">
          <EmptyState title={t(config.emptyTitleKey)} description={t(config.emptyDescriptionKey)} />
        </div>
      </section>
    );
  }

  const renderRows = (sectionGroups: IProductGroup[]) =>
    sectionGroups.map((group) => {
      const lineSummary = [...(summary?.employerLines ?? []), ...(summary?.voluntaryLines ?? [])].find(
        (l) => l.key === group.key
      );
      return (
        <ProductRow
          key={group.key}
          group={group}
          selection={selection[group.key]}
          age={age}
          employeesSize={employeesSize}
          averageAge={averageAge}
          averageAgeError={averageAgeError}
          onToggle={() => onToggle(group.key)}
          onSelectAddon={(addonId) => onSelectAddon(group.key, addonId)}
          onTobacco={(status) => onTobacco(group.key, status)}
          onAverageAgeChange={onAverageAgeChange}
          onAgeInputModeChange={(mode) => onAgeInputModeChange(group.key, mode)}
          onCensusApply={(avgAge, censusSummary, fileObj, fileName, rowCount) =>
            onCensusApply(group.key, avgAge, censusSummary, fileObj, fileName, rowCount)
          }
          onCensusRemove={() => onCensusRemove(group.key)}
          onUpdateHeadcount={onUpdateHeadcount}
          productTotal={lineSummary?.total}
          censusFile={censusFile}
        />
      );
    });

  return (
    <section className={`quote-products${disabled ? ' quote-products--disabled' : ''}`}>
      <PhaseHeader
        number={number}
        className="quote-products__header"
        title={t(config.titleKey)}
        status={selectedCount > 0 ? `✓ ${t(config.selectedKey, { count: selectedCount })}` : undefined}
      />

      {disabled && <p className="quote-products__hint">{t(blockedByPlan ? config.planHintKey : config.hintKey)}</p>}

      <div className="quote-products__content">
        {config.types.map((type, index) => {
          const sectionGroups = groups.filter((group) => group.type === type);
          if (sectionGroups.length === 0) return null;

          if (!config.showTypeHeaders) {
            return (
              <div key={type} className="quote-products__rows">
                {renderRows(sectionGroups)}
              </div>
            );
          }

          const headerClass = [
            'quote-products__section-header',
            index === 0 ? 'quote-products__section-header--first' : ''
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <div key={type} className="quote-products__section">
              <div className={headerClass}>
                <div className="quote-products__section-title">{t(TYPE_LABEL_KEYS[type])}</div>
              </div>
              <div className="quote-products__rows">{renderRows(sectionGroups)}</div>
            </div>
          );
        })}

        {error && <div className="quote-products__error">{error}</div>}
      </div>
    </section>
  );
};

export default ProductsSection;
